import { useCookies } from "react-cookie";
import { useCallback, useMemo } from "react";
import {
  ACCESS_TOKEN_COOKIE,
  ID_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  TokenKey,
} from "../constants";
import { getTokenExpiry, isTokenExpired } from "../utils/auth";

const EXPIRY_HOURS = 60 * 60 * 1000;

const useAuthenticationTokens = () => {
  const [cookies, setCookie, removeCookie] = useCookies([
    ACCESS_TOKEN_COOKIE,
    REFRESH_TOKEN_COOKIE,
    ID_TOKEN_COOKIE,
  ]);

  const accessToken = useMemo(() => {
    return cookies[ACCESS_TOKEN_COOKIE];
  }, [cookies]);

  const setToken = useCallback(
    (key: TokenKey, value: string): void => {
      const options: NonNullable<Parameters<typeof setCookie>[2]> = {
        path: "/",
        secure: window.location.protocol === "https:",
        sameSite: "strict",
      };

      const expiry = getTokenExpiry(value);
      if (expiry) {
        options.expires = new Date(expiry);
      } else {
        if (key === REFRESH_TOKEN_COOKIE) {
          // Default to 30 days
          options.expires = new Date(Date.now() + 30 * 24 * EXPIRY_HOURS);
        } else {
          // Default to 1 hour
          options.expires = new Date(Date.now() + EXPIRY_HOURS);
        }
      }

      setCookie(key, value, options);

      // Also store in sessionStorage for faster access (except refresh token)
      if (key !== REFRESH_TOKEN_COOKIE) {
        sessionStorage.setItem(key, value);
      }
    },
    [setCookie],
  );

  const getToken = useCallback(
    (key: TokenKey): string | null => {
      if (key === REFRESH_TOKEN_COOKIE) {
        return cookies[key] || null;
      }

      // For access/id tokens, try sessionStorage first
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue && !isTokenExpired(sessionValue)) {
        return sessionValue;
      }

      // Clear expired sessionStorage value
      if (sessionValue && isTokenExpired(sessionValue)) {
        sessionStorage.removeItem(key);
      }

      // Fall back to cookies
      const cookieValue = cookies[key];
      if (cookieValue && !isTokenExpired(cookieValue)) {
        // Sync valid cookie value to session storage
        sessionStorage.setItem(key, cookieValue);
        return cookieValue;
      }

      return null;
    },
    [cookies],
  );

  const removeToken = useCallback(
    (key: TokenKey): void => {
      removeCookie(key, { path: "/" });
      sessionStorage.removeItem(key);
    },
    [removeCookie],
  );

  const clearAllTokens = useCallback((): void => {
    const tokenKeys: TokenKey[] = [
      ACCESS_TOKEN_COOKIE,
      REFRESH_TOKEN_COOKIE,
      ID_TOKEN_COOKIE,
    ];
    tokenKeys.forEach((cookieKey) => {
      removeToken(cookieKey);
    });
  }, [removeToken]);

  return {
    accessToken,
    setToken,
    getToken,
    removeToken,
    clearAllTokens,
    isTokenExpired,
    getTokenExpiry,
  };
};

export default useAuthenticationTokens;
